import axiosInstance, { refreshOnce } from './axiosInstance';

/**
 * 이메일 로그인
 * 토큰이 응답 헤더에 담겨 오므로 인터셉터 없는 순수 axios 사용.
 * baseURL은 axiosInstance에서 단일 관리.
 */
export const login = async (email, password) => {
  // withCredentials: true (axiosInstance 기본값) → 서버가 Set-Cookie로 토큰 발급
  const res = await axiosInstance.post('/api/login', { email, password });

  const hasRoleInfo = (d) => d?.isTalent != null || d?.isProducer != null || d?.primaryRole;

  let loginData = null;
  if (hasRoleInfo(res.data)) {
    loginData = res.data;
  } else if (typeof res.data === 'string' && res.data.trim()) {
    try {
      const parsed = JSON.parse(res.data);
      if (hasRoleInfo(parsed)) loginData = parsed;
    } catch {}
  }

  if (!loginData) return { isTalent: null, isProducer: null, profiles: null };

  // isQnaAdmin / id 등 상세 권한은 /api/users/me에만 있으므로 항상 호출
  // 쿠키가 이미 설정되었으므로 별도 헤더 없이 호출 가능
  try {
    const meRes = await axiosInstance.get('/api/users/me');
    return { ...loginData, ...meRes.data };
  } catch {}

  return loginData;
};


/**
 * 온보딩 완료 후 JWT에 새 role 반영을 위해 호출
 * 쿠키가 자동 전송되므로 별도 헤더 불필요
 *
 * 예전 POST /api/auth는 삭제됐다(404). 인터셉터와 같은 갱신 함수를 써야
 * 동시에 진행 중인 갱신과 겹쳐 리프레시 토큰이 폐기되는 일이 없다.
 */
export const reissueToken = async () => {
  await refreshOnce();
};

/**
 * 로그아웃 — 서버 httpOnly 쿠키 만료 요청
 */
export const logout = async () => {
  await axiosInstance.post('/api/auth/logout');
};

/**
 * 휴대폰 번호 중복 확인
 * @param {string} phone
 * @returns {{ exists: boolean }}
 */
export const checkPhone = async (phone) => {
  const res = await axiosInstance.get('/api/auth/check-phone', { params: { phone } });
  return res.data;
};

/**
 * 휴대폰 인증코드 발송
 * @param {string} phone - 예: "01012345678"
 * @param {"SIGNUP"|"FIND_ID"} purpose - 미전송 시 백엔드 기본값 SIGNUP
 */
export const sendPhoneCode = async (phone, purpose) => {
  const body = purpose ? { phone, purpose } : { phone };
  const res = await axiosInstance.post('/api/users/phone-code', body);
  return res.data; // { success, errorCode }
};

/**
 * 휴대폰 OTP 검증 + 로그인
 * verified + isNewUser 조합으로 로그인/회원가입 분기 처리는 호출부에서 수행
 */
export const verifyPhoneLogin = async (phone, code) => {
  const res = await axiosInstance.post('/api/auth/nlogin', { phone, code });
  // 서버가 Set-Cookie로 토큰 발급 — 별도 저장 불필요
  return res.data; // { verified, isNewUser, errorCode? }
};

/**
 * 회원가입
 * @param {{ email, password, phone, isTalent: boolean, isProducer: boolean,
 *           termsAgreed: boolean, privacyAgreed: boolean,
 *           marketingAgreed: boolean, aiTrainingAgreed: boolean }} data
 */
export const signup = async (data) => {
  const res = await axiosInstance.post('/api/auth/register', data);
  return res.data; // { id }
};

/**
 * 이메일 중복 확인
 * @returns {{ exists: boolean }}
 */
export const checkEmail = async (email) => {
  const res = await axiosInstance.post('/api/auth/email-check', { email });
  return res.data;
};

/**
 * 그 이메일에 이미 '대행(MANAGED) 계정'이 발급돼 있는지.
 *
 * 이 경우 새로 가입하면 안 된다 — 기존 공고·지원자가 옛 계정에 남아
 * 회사 입장에서는 자기 공고가 하나도 안 보인다. 로그인 후 전환이 맞다.
 * 서버가 어느 키로 알려줄지 확정 전이라 후보를 모두 받아준다.
 */
export const isManagedExisting = (res) => Boolean(
  res && (
    res.managed === true
    || res.isManaged === true
    || res.accountType === 'MANAGED'
    || res.code === 'MANAGED_ACCOUNT_EXISTS'
    || res.errorCode === 'MANAGED_ACCOUNT_EXISTS'
    || res.reason === 'MANAGED_ACCOUNT_EXISTS'
  ),
);

/**
 * 비밀번호 재설정용 이메일 인증코드 발송
 * @param {string} email
 * @returns {{ success: boolean, errorCode?: string }}
 */
export const sendEmailCode = async (email) => {
  const res = await axiosInstance.post('/api/auth/email-code', { email });
  return res.data;
};

/**
 * 이메일 인증코드 확인
 * @param {string} email
 * @param {string} code
 * @returns {{ verified: boolean, errorCode?: string }}
 */
export const verifyEmailCode = async (email, code) => {
  const res = await axiosInstance.post('/api/auth/email-verify', { email, code });
  return res.data;
};

/**
 * 비밀번호 변경
 */
export const changePassword = async (email, newPassword) => {
  await axiosInstance.patch('/api/auth/password', { email, newPassword });
};

/**
 * 로그인 상태에서 내 비밀번호 변경 (세션 기준)
 *
 * 기존 PATCH /api/auth/password는 이메일 OTP 기반(비로그인용)이라 별개다.
 * mustChangePassword=true인 최초 변경에는 currentPassword를 생략할 수 있다.
 */
export const changeMyPassword = async ({ currentPassword, newPassword }) => {
  await axiosInstance.patch('/api/users/me/password', {
    currentPassword: currentPassword || undefined,
    newPassword,
  });
};

/**
 * 휴대폰 번호로 아이디 찾기
 * @param {string} phone
 * @param {string} code
 * @returns {{ verified: boolean, email?: string, errorCode?: string }}
 */
export const findIdByPhone = async (phone, code) => {
  const res = await axiosInstance.post('/api/auth/find-id', { phone, code });
  return res.data; // { verified, email?, errorCode? }
};
